import React, { PropsWithChildren, forwardRef } from 'react';
import styled from 'styled-components';
import Text from '../Text';
import type { Typescales } from '@/common/view/typescales';
import {
  Size,
  Skin,
  getButtonTypo,
  getHeight,
  getPadding,
  getBgColor,
  getBorderColor,
  getTextColor,
  getHoverBorderColor,
  getDisabledBgColor,
  getDisabledBorderColor,
  getDisabledTextColor,
} from './util';
import Loader from './Loader';

export type ButtonSize = Size;
export type ButtonSkin = Skin;
type TextAlign = 'left' | 'center' | 'right';
export interface ButtonProps extends PropsWithChildren {
  type?: 'button' | 'submit' | 'reset';
  skin: Skin;
  size?: Size;
  typo?: Typescales;
  loading?: boolean;
  stretch?: boolean;
  disabled?: boolean;
  error?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  className?: string;
  hidden?: boolean;
  textAlign?: TextAlign;
}

const textAlignMap: Record<TextAlign, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};
export const StyledButton = styled.button<{
  $stretch?: boolean;
  $skin: Skin;
  $size: Size;
  $hidden?: boolean;
  $textAlign?: TextAlign;
  $error?: boolean;
}>`
  position: relative;
  display: flex;
  justify-content: ${({ $textAlign }) => textAlignMap[$textAlign || 'center']};
  align-items: center;
  white-space: nowrap;
  ${({ $hidden }) => ($hidden ? 'visibility: hidden;' : '')}
  ${({ $stretch }) => ($stretch ? 'width: 100%;' : '')}
  ${({ $stretch }) => ($stretch ? 'justify-content: center;' : '')}
  border-radius: 8px;
  border: 1px solid
    ${({ $skin, $error, theme }) => ($error ? theme.colors.red50 : getBorderColor($skin))};
  height: ${({ $size }) => getHeight($size)};
  padding: ${({ $size }) => getPadding($size)};
  background-color: ${({ $skin }) => getBgColor($skin)};
  color: ${({ $skin }) => getTextColor($skin)};
  &:hover {
    border-color: ${({ $skin, $error, theme }) =>
      $error ? theme.colors.red50 : getHoverBorderColor($skin)};
  }
  &:disabled {
    cursor: not-allowed;
    background-color: ${({ $skin }) => getDisabledBgColor($skin)};
    border-color: ${({ $skin }) => getDisabledBorderColor($skin)};
    color: ${({ $skin }) => getDisabledTextColor($skin)};
  }
  &:focus {
    outline: 1px solid ${({ $skin }) => getBorderColor($skin)};
  }

  &.loading {
    & > div:first-child {
      visibility: hidden;
    }
  }
`;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = 'button',
      skin,
      size = 'medium',
      typo = getButtonTypo(size),
      loading,
      stretch = false,
      disabled,
      error,
      onClick,
      children,
      className,
      hidden = false,
      textAlign = 'center',
    },
    ref,
  ) => {
    return (
      <StyledButton
        ref={ref}
        type={type}
        className={`${className} ${loading ? 'loading' : ''}`}
        $skin={skin}
        $size={size}
        $stretch={stretch}
        $hidden={hidden}
        $textAlign={textAlign}
        disabled={loading || disabled}
        $error={error && !disabled}
        onClick={onClick}>
        <Text typo={typo} color="inherit">
          {children}
        </Text>
        {loading && <Loader size={size} typo={typo} />}
      </StyledButton>
    );
  },
);

Button.displayName = 'Button';
export default Button;
export type { Size, Skin } from './util';
